import React, { FC, FormEvent, useContext, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import styles from './ContactPage.module.scss';
import { FileUploader } from '../../../components/FileUploader';
import { AuthContext } from '../../../context/auth';
import Alert from 'react-bootstrap/Alert';
import { LoginButton } from '../../../components/UI/LoginButton';
import { useContactSupportMutation } from './mutation';
import axios from 'axios';

interface ValidationErrors {
  email?: string;
  subject?: string;
  message?: string;
  customerNumber?: string;
}

interface BackendValidationResponse {
  message?: string;
  errors?: Record<string, string[]>;
}

const ContactPage: FC = () => {
  const authContext = useContext(AuthContext);
  const customerNr = localStorage.getItem('blue_customerNumber') || '';

  const { isAuthenticated } = authContext;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [email, setEmail] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [customerNumber, setCustomerNumber] = useState<string>('');
  const [questionType, setQuestionType] = useState<string>('inhalt-melden');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [formSubmited, setFormSubmited] = useState<{ status: boolean; message: string; error: boolean }>({
    status: false,
    message: '',
    error: false,
  });
  const { mutateContactSupport } = useContactSupportMutation();

  const clearValidationError = (field: keyof ValidationErrors) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const mapBackendErrorsToForm = (backendErrors?: Record<string, string[]>): ValidationErrors => {
    if (!backendErrors) {
      return {};
    }

    const mappedErrors: ValidationErrors = {};
    const fieldMap: Record<string, keyof ValidationErrors> = {
      user_email: 'email',
      subject: 'subject',
      message: 'message'
    };

    Object.entries(backendErrors).forEach(([backendField, messages]) => {
      const formField = fieldMap[backendField];
      if (formField && messages?.length) {
        mappedErrors[formField] = messages[0];
      }
    });

    return mappedErrors;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData();
    console.log(formData);
    formData.append('user_email', email);
    formData.append('subject', subject);
    formData.append('message', message);
    formData.append('question_type', questionType);
    if (customerNr && customerNr !== '') {
      formData.append('customerNr', customerNr);
    } else {
      formData.append('customerNr', customerNumber);
    }
    if (selectedFile) {
      formData.append('file', selectedFile);
    }

    setIsLoading(true);

    mutateContactSupport(formData, {
      onSuccess() {
        setFormSubmited({
          status: true,
          message: 'E-Mail wurde erfolgreich gesendet',
          error: false,
        });
        setMessage('');
        setEmail('');
        setSubject('');
        setSelectedFile(null);
        setIsLoading(false);
        setCustomerNumber('');
        setQuestionType('');
        setValidationErrors({});
      },
      onError(error) {
        setIsLoading(false);
        if (axios.isAxiosError(error)) {
          const responseData = error.response?.data as BackendValidationResponse | undefined;
          const backendFieldErrors = mapBackendErrorsToForm(responseData?.errors);

          if (Object.keys(backendFieldErrors).length > 0) {
            setValidationErrors(backendFieldErrors);
          }

          setFormSubmited({
            status: false,
            error: true,
            message: 'Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut.',
          });
          return;
        }

        setFormSubmited({
          status: false,
          error: true,
          message: 'Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut.',
        });
      },
    });
  };
  return (
    <Container>
      <Row>
        <Col sm={12}>
          <h2>KONTAKT ZUM KUNDENSERVICE</h2>
        </Col>
      </Row>
      <Row>
        <Col md={5} sm={12}>
          <p>Bitte füllen Sie das nachfolgende Formular aus.</p>
          <p>Wir werden Ihnen so schnell wie möglich antworten.</p>
        </Col>
        <Col md={7} sm={12}>
          <form encType="multipart/form-data" onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor="">E-Mail*</label>
              <input 
                value={email} 
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearValidationError('email');
                }} 
                type="email" 
                className={validationErrors.email ? styles.error : ''}
              />
              {validationErrors.email && (
                <div className={styles.errorMessage}>
                  {validationErrors.email}
                </div>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="">Betreff*</label>
              <input 
                value={subject} 
                onChange={(e) => {
                  setSubject(e.target.value);
                  clearValidationError('subject');
                }} 
                type="text" 
                className={validationErrors.subject ? styles.error : ''}
              />
              {validationErrors.subject && (
                <div className={styles.errorMessage}>
                  {validationErrors.subject}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="requestType">Art der Anfrage*</label>
              <select id="requestType" value={questionType} required onChange={(e) => setQuestionType(e.target.value)}>
                <option value="inhalt-melden">Inhalt melden</option>
                <option value="entfernung-von-inhalten-beantragen">Entfernung von Inhalten beantragen</option>
                <option value="urheberrechtsverletzung-melden">Eine Urheberrechtsverletzung melden</option>
                <option value="datenschutzanfrage">Datenschutzanfrage</option>
                <option value="missbrauch-melden">Missbrauch melden</option>
                <option value="allgemeine-beschwerde">Allgemeine Beschwerde</option>
                <option value="technisches-problem-melden">Technisches Problem melden</option>
                <option value="fragen-zu-rechnungen-oder-zahlungen">Fragen zu Rechnungen oder Zahlungen</option>
                <option value="feedback-und-verbesserungsvorschlaege">Feedback und Verbesserungsvorschläge</option>
                <option value="andere">Andere</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="">Ihre Nachricht*</label>
              <textarea 
                value={message} 
                onChange={(e) => {
                  setMessage(e.target.value);
                  clearValidationError('message');
                }} 
                cols={35} 
                rows={4} 
                className={validationErrors.message ? styles.error : ''}
              />
              {validationErrors.message && (
                <div className={styles.errorMessage}>
                  {validationErrors.message}
                </div>
              )}
            </div>

            {isAuthenticated ? (
              // TODO: Isn't working cause BE doesn't receive FormData
              <div>
                <FileUploader
                  submited={formSubmited.status}
                  onFileSelectSuccess={(file) => setSelectedFile(file)}
                  onFileSelectError={({ error }) => alert(error)}
                />
              </div>
            ) : (
              <div className={styles.formGroup}>
                <label htmlFor="">Sky Kundennummer</label>
                <input
                  value={customerNumber}
                  onChange={(e) => {
                    setCustomerNumber(e.target.value);
                    clearValidationError('customerNumber');
                  }}
                  type="text"
                  className={validationErrors.customerNumber ? styles.error : ''}
                />
                {validationErrors.customerNumber && (
                  <div className={styles.errorMessage}>
                    {validationErrors.customerNumber}
                  </div>
                )}
              </div>
            )}
            <div className={styles.formGroup}>
              {formSubmited.error && (
                <Alert style={{ width: '60%' }} show={formSubmited.error} variant={'danger'}>
                  {formSubmited.message}
                </Alert>
              )}
              {!formSubmited.error && formSubmited.status && (
                <Alert style={{ width: '60%' }} show={formSubmited.status} variant={'primary'}>
                  {formSubmited.message}
                </Alert>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'end', width: '60%' }}>
              <LoginButton loading={isLoading} handleLogin={(e) => handleSubmit(e)} />
            </div>
          </form>
        </Col>
      </Row>
      <Row>
        <Col sm={12} md={5}>
          <p>Wir geben Ihre persönlichen Daten nicht an Dritte weiter.</p>
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <p>
            Wenn Sie Fragen zu Ihren Zahlungen über SEGPAY (PayPal und Kreditkarte) haben, können Sie sich auch
            jederzeit an den{' '}
            <a
              href="https://segpay.com/contact-us/#"
              target="_blank"
              rel="noopener noreferrer"
              title="https://epoch.com"
            >
              Segpay-Support
            </a>{' '}
            wenden.
            <span> Wenn Sie Fragen zu Ihren Zahlungen über EPOCH (PayPal) haben, können Sie sich auch jederzeit an den  <span>{' '}
              <a
                href="https://epoch.com"
                target="_blank"
                rel="noopener noreferrer"
                title="https://epoch.com"
              >
              EPOCH-Support
            </a>{' '}</span>  wenden.</span>

          </p>
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <p>
            Jede eingehende Beschwerde wird bearbeitet. Der Antragsteller erhält innerhalb von 5 Werktagen oder früher
            eine Rückmeldung.
          </p>
        </Col>
      </Row>
    </Container>
  );
};

export default ContactPage;
